<div class="profile-container">
  <h1>My Profile</h1>

  <!-- User Header -->
  <div class="profile-header" *ngIf="currentUser">
    <div class="user-avatar">
      <img [src]="currentUser.recentPhoto" [alt]="currentUser.name" onerror="this.src='https://via.placeholder.com/100x100/4CAF50/ffffff?text=' + this.alt.charAt(0)">
    </div>
    <div class="user-info">
      <h2>{{ currentUser.name }}</h2>
      <p>{{ currentUser.mobile }}</p>
      <p>Member since {{ formatDate(currentUser.registrationDate) }}</p>
    </div>
    <div class="profile-actions">
      <button (click)="logout()" class="logout-btn">Logout</button>
    </div>
  </div>

  <!-- Tab Navigation -->
  <div class="tab-navigation">
    <button 
      *ngFor="let tab of ['overview', 'profile', 'doses', 'orders']"
      [class.active]="activeTab === tab"
      (click)="switchTab(tab)"
      class="tab-btn">
      {{ tab | titlecase }}
    </button>
  </div>

  <!-- Tab Content -->
  <div class="tab-content">

    <!-- Overview Tab -->
    <div *ngIf="activeTab === 'overview'" class="tab-panel">
      <div class="overview-grid">
        
        <!-- Health Stats -->
        <div class="stats-card">
          <h3>Health Statistics</h3>
          <div class="stat-item">
            <label>Current Height:</label>
            <span>{{ currentUser?.currentHeight }} cm</span>
          </div>
          <div class="stat-item">
            <label>Current Weight:</label>
            <span>{{ currentUser?.currentWeight }} kg</span>
          </div>
          <div class="stat-item">
            <label>BMI:</label>
            <span>{{ calculateBMI() | number:'1.1-1' }} ({{ getBMICategory() }})</span>
          </div>
        </div>

        <!-- Dose Progress -->
        <div class="progress-card">
          <h3>Dose Progress</h3>
          <div class="progress-circle">
            <div class="circle-progress" [style.--progress]="getDoseProgressPercentage() + '%'">
              <span>{{ getDoseProgressPercentage() | number:'1.0-0' }}%</span>
            </div>
          </div>
          <p>{{ (doseEligibility?.completedDoses?.length || 0) }}/3 doses completed</p>
          <div class="next-dose" *ngIf="!doseEligibility?.currentActiveDose">
            <p><strong>Next:</strong> {{ getNextDose() }}</p>
            <button (click)="navigateToProducts()" class="btn-primary">Shop Now</button>
          </div>
          <div class="active-dose" *ngIf="doseEligibility?.currentActiveDose">
            <p><strong>Active Dose:</strong> {{ doseEligibility?.currentActiveDose?.doseType | titlecase }}</p>
            <p>{{ getActiveDoseDaysRemaining() }} days remaining</p>
          </div>
        </div>

        <!-- Recent Orders -->
        <div class="orders-card">
          <h3>Recent Orders</h3>
          <div *ngIf="userOrders.length === 0" class="no-orders">
            <p>No orders found</p>
            <button (click)="navigateToProducts()" class="btn-secondary">Start Shopping</button>
          </div>
          <div *ngFor="let order of userOrders.slice(0, 3)" class="order-item">
            <div class="order-info">
              <p><strong>Order #{{ order.id }}</strong></p>
              <p>{{ formatDate(order.orderDate) }}</p>
            </div>
            <div class="order-status">
              <span [style.color]="getOrderStatusColor(order.status)">{{ order.status | titlecase }}</span>
            </div>
            <div class="order-amount">
              ₹{{ order.totalAmount | number:'1.2-2' }}
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Profile Tab -->
    <div *ngIf="activeTab === 'profile'" class="tab-panel">
      <div class="profile-form">
        <h3>Personal Information</h3>
        
        <form [formGroup]="profileForm" (ngSubmit)="updateProfile()">
          <div class="form-grid">
            
            <div class="form-group">
              <label for="name">Full Name</label>
              <input id="name" formControlName="name" type="text" [readonly]="!isEditing">
              <div *ngIf="name?.invalid && (name?.dirty || name?.touched)" class="error">
                <div *ngIf="name?.errors?.['required']">Name is required.</div>
              </div>
            </div>

            <div class="form-group">
              <label for="email">Email</label>
              <input id="email" formControlName="email" type="email" [readonly]="!isEditing">
              <div *ngIf="email?.invalid && (email?.dirty || email?.touched)" class="error">
                <div *ngIf="email?.errors?.['required']">Email is required.</div>
                <div *ngIf="email?.errors?.['email']">Enter a valid email.</div>
              </div>
            </div>

            <div class="form-group">
              <label for="currentWeight">Current Weight (kg)</label>
              <input id="currentWeight" formControlName="currentWeight" type="number" [readonly]="!isEditing">
              <div *ngIf="currentWeight?.invalid && (currentWeight?.dirty || currentWeight?.touched)" class="error">
                <div *ngIf="currentWeight?.errors?.['required']">Weight is required.</div>
              </div>
            </div>

            <div class="form-group">
              <label for="currentHeight">Current Height (cm)</label>
              <input id="currentHeight" formControlName="currentHeight" type="number" [readonly]="!isEditing">
              <div *ngIf="currentHeight?.invalid && (currentHeight?.dirty || currentHeight?.touched)" class="error">
                <div *ngIf="currentHeight?.errors?.['required']">Height is required.</div>
              </div>
            </div>

            <div class="form-group full-width">
              <label for="fullAddress">Address</label>
              <textarea id="fullAddress" formControlName="fullAddress" [readonly]="!isEditing"></textarea>
              <div *ngIf="fullAddress?.invalid && (fullAddress?.dirty || fullAddress?.touched)" class="error">
                <div *ngIf="fullAddress?.errors?.['required']">Address is required.</div>
              </div>
            </div>

            <div class="form-group">
              <label for="altMobile">Alternate Mobile</label>
              <input id="altMobile" formControlName="altMobile" type="text" [readonly]="!isEditing">
              <div *ngIf="altMobile?.invalid && (altMobile?.dirty || altMobile?.touched)" class="error">
                <div *ngIf="altMobile?.errors?.['pattern']">Enter a valid mobile number.</div>
              </div>
            </div>

          </div>

          <div class="form-actions">
            <button type="button" *ngIf="!isEditing" (click)="toggleEdit()" class="btn-primary">Edit Profile</button>
            <div *ngIf="isEditing">
              <button type="submit" [disabled]="isLoading" class="btn-primary">
                {{ isLoading ? 'Saving...' : 'Save Changes' }}
              </button>
              <button type="button" (click)="toggleEdit()" class="btn-secondary">Cancel</button>
            </div>
          </div>

          <div *ngIf="errorMessage" class="error-message">{{ errorMessage }}</div>
          <div *ngIf="successMessage" class="success-message">{{ successMessage }}</div>
        </form>
      </div>
    </div>

    <!-- Doses Tab -->
    <div *ngIf="activeTab === 'doses'" class="tab-panel">
      <div class="doses-content">
        <h3>Dose History</h3>
        
        <!-- Dose Progress Visual -->
        <div class="dose-timeline">
          <div class="timeline-item" 
               [class.completed]="doseEligibility?.completedDoses?.includes('green')"
               [class.active]="doseEligibility?.currentActiveDose?.doseType === 'green'">
            <div class="timeline-marker" style="background-color: #4CAF50;">1</div>
            <div class="timeline-content">
              <h4>Green Dose (Foundation)</h4>
              <p>Initiates height growth process</p>
            </div>
          </div>

          <div class="timeline-item" 
               [class.completed]="doseEligibility?.completedDoses?.includes('yellow')"
               [class.active]="doseEligibility?.currentActiveDose?.doseType === 'yellow'">
            <div class="timeline-marker" style="background-color: #FFC107;">2</div>
            <div class="timeline-content">
              <h4>Yellow Dose (Enhancement)</h4>
              <p>Maximizes growth potential</p>
            </div>
          </div>

          <div class="timeline-item" 
               [class.completed]="doseEligibility?.completedDoses?.includes('red')"
               [class.active]="doseEligibility?.currentActiveDose?.doseType === 'red'">
            <div class="timeline-marker" style="background-color: #F44336;">3</div>
            <div class="timeline-content">
              <h4>Red Dose (Maximum)</h4>
              <p>Final growth consolidation</p>
            </div>
          </div>
        </div>

        <!-- Dose History Table -->
        <div class="dose-history" *ngIf="doseHistory.length > 0">
          <h4>Detailed History</h4>
          <div class="history-table">
            <div class="table-header">
              <span>Dose</span>
              <span>Start Date</span>
              <span>End Date</span>
              <span>Status</span>
              <span>Duration</span>
            </div>
            <div class="table-row" *ngFor="let dose of doseHistory">
              <span>{{ dose.doseType | titlecase }} Dose</span>
              <span>{{ formatDate(dose.startDate) }}</span>
              <span>{{ formatDate(dose.endDate) }}</span>
              <span>{{ dose.status | titlecase }}</span>
              <span>30 days</span>
            </div>
          </div>
        </div>

        <div *ngIf="doseHistory.length === 0" class="no-doses">
          <p>No dose history found</p>
          <button (click)="navigateToProducts()" class="btn-primary">Start Your Height Gain Journey</button>
        </div>
      </div>
    </div>

    <!-- Orders Tab -->
    <div *ngIf="activeTab === 'orders'" class="tab-panel">
      <div class="orders-content">
        <h3>Order History</h3>
        
        <div *ngIf="userOrders.length === 0" class="no-orders">
          <p>No orders found</p>
          <button (click)="navigateToProducts()" class="btn-primary">Start Shopping</button>
        </div>

        <div class="orders-list" *ngIf="userOrders.length > 0">
          <div class="order-card" *ngFor="let order of userOrders">
            <div class="order-header">
              <div class="order-id">
                <h4>Order #{{ order.id }}</h4>
                <p>{{ formatDate(order.orderDate) }}</p>
              </div>
              <div class="order-status">
                <span [style.color]="getOrderStatusColor(order.status)">{{ order.status | titlecase }}</span>
              </div>
            </div>

            <div class="order-items">
              <div class="item" *ngFor="let item of order.products">
                <img [src]="item.image" [alt]="item.name">
                <div class="item-details">
                  <p><strong>{{ item.name }}</strong></p>
                  <p>{{ item.doseType | titlecase }} Dose</p>
                  <p>Qty: {{ item.cartQuantity }}</p>
                </div>
                <div class="item-price">
                  ₹{{ ((item.price ?? 0) * item.cartQuantity) | number:'1.2-2' }}
                </div>
              </div>
            </div>

            <div class="order-footer">
              <div class="order-total">
                <strong>Total: ₹{{ order.totalAmount | number:'1.2-2' }}</strong>
                <span *ngIf="order.discountAmount && order.discountAmount > 0">
                  (Saved ₹{{ order.discountAmount | number:'1.2-2' }})
                </span>
              </div>
              <div class="order-actions">
                <button (click)="viewOrder(order.id)" class="btn-secondary">View Details</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
